import { useEffect, useRef } from 'react'
import { ENV } from '@forwardflow/helpers/configs'
import { useAppStore } from '@forwardflow/store'

/**
 * Hook for establishing and managing a Server-Sent Events connection
 * for real-time conversation updates
 */
export const useConversationSSE = (onUpdate: () => void) => {
   const { currentOrganization } = useAppStore()
   // Use ref to avoid recreating SSE connection when onUpdate changes
   const onUpdateRef = useRef(onUpdate)

   // Keep the ref up to date
   useEffect(() => {
      onUpdateRef.current = onUpdate
   }, [onUpdate])

   useEffect(() => {
      if (!currentOrganization) {
         return
      }

      let eventSource: EventSource | null = null
      let retryCount = 0
      const maxRetryCount = 10
      const baseRetryDelay = 1000
      const maxRetryDelay = 30000

      const createEventSource = () => {
         if (eventSource) {
            eventSource.close()
         }

         const url = `${ENV.PUBLIC_FF_URL}sse/conversations?org=${currentOrganization}`

         eventSource = new EventSource(url)

         eventSource.onopen = () => {
            retryCount = 0
         }

         eventSource.addEventListener('recent', (event) => {
            try {
               const data = JSON.parse(event.data)
               if (data) {
                  onUpdateRef.current()
               }
            } catch (error) {
               console.error('Error parsing SSE event data:', error)
            }
         })

         eventSource.onerror = (err) => {
            console.error('SSE connection error:', err)

            if (eventSource) {
               eventSource.close()
               eventSource = null
            }

            if (retryCount < maxRetryCount) {
               const delay = Math.min(
                  baseRetryDelay * Math.pow(2, retryCount),
                  maxRetryDelay
               )
               setTimeout(() => {
                  retryCount++
                  createEventSource()
               }, delay)
            } else {
               console.error(
                  `Failed to reconnect after ${maxRetryCount} attempts`
               )
            }
         }

         return eventSource
      }

      createEventSource()

      return () => {
         if (eventSource) {
            eventSource.close()
            eventSource = null
         }
      }
   }, [currentOrganization])
}
